/* eslint-disable @typescript-eslint/no-explicit-any */
import { Box, Grid, Stack, useMediaQuery, useTheme, Tabs, Tab } from '@mui/material';
import { useTranslation } from 'react-i18next';
import FinanceBox from './components/FinanceBox';
import { useContext, useEffect, useState, useCallback } from 'react';
import PageArea from '../../../components/shared/PageArea';
import PageTitle from '../../../components/shared/PageTitle';
import { UserContext } from '../../../components/shared/useUser';
import { client, FinanceTableRowModel } from '../../../app/services/api/orderManagementClient';
import useLogError from '../../../hooks/useLogError';
import { generateSasDownload } from '../../../utils';
import MessagesSnackbar from '../../../components/shared/MessagesSnackbar';

const Finance = () => {
  const { t } = useTranslation('finance');
  const theme = useTheme();
  const isMobile = useMediaQuery(`(max-width:${theme.breakpoints.values.md}px)`);
  const [tab, setTab] = useState<'invoices' | 'creditNotes' | 'statements' | 'rebates' | 'loyalty'>('invoices');
  const [page, setPage] = useState(0);
  const [data, setData] = useState<FinanceTableRowModel[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [totalPages, setTotalPages] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDownloadMessage, setShowDownloadMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showError, setShowError] = useState(false);
  const [debouncedSearch, setDebouncedSearch] = useState(searchTerm);

  const { hasPermissionTo, selectedStore } = useContext(UserContext);
  const { logError } = useLogError();

  useEffect(() => {
    setPage(0);
    const handler = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 800);

    return () => clearTimeout(handler);
  }, [searchTerm]);

  useEffect(() => {
    fetchFinanceRows(page, debouncedSearch);
  }, [tab, page, debouncedSearch]);

  const toCamelCase = useCallback((str: string) => str.charAt(0).toLowerCase() + str.slice(1), []);

  const mapFinanceRow = useCallback(
    (row: any): FinanceTableRowModel => {
      const mapped: any = {};
      Object.keys(row).forEach(key => {
        mapped[toCamelCase(key)] = row[key];
      });
      return mapped as FinanceTableRowModel;
    },
    [toCamelCase],
  );

  const fetchFinanceRows = async (currentPage = 0, currentSearch = '') => {
    if (!hasPermissionTo(['FinanceView'])) {
      setShowError(true);
      setErrorMessage(t('noPermissions'));
      logError(t('noPermissions'));
      return;
    }
    if (!selectedStore?.storeNumber) {
      setShowError(true);
      setErrorMessage(t('invalidStore'));
      logError(t('invalidStore'));
      return;
    }

    setIsLoading(true);
    try {
      const response = await client.getFinanceTableRows(
        selectedStore.storeNumber,
        tab,
        currentSearch,
        currentPage,
      );
      setData(response.data.items.map(mapFinanceRow));
      setTotalPages(response.data.totalPages);
    } catch (err) {
      logError(err);
    } finally {
      setIsLoading(false);
    }
  };

  const onDocumentDownload = async (documentId: number) => {
    setShowError(false);
    setShowDownloadMessage(true);

    if (!hasPermissionTo(['FinanceView'])) {
      setShowDownloadMessage(false);
      setShowError(true);
      setErrorMessage(t('noPermissions'));
      logError(t('noPermissions'));
      return;
    }
    if (!selectedStore?.storeNumber) {
      setShowDownloadMessage(false);
      setShowError(true);
      setErrorMessage(t('invalidStore'));
      logError(t('invalidStore'));
      return;
    }

    try {
      const result = await client.downloadFinancialBlob(documentId, tab, selectedStore.storeNumber);
      if (result) {
        generateSasDownload(result.data.Data, result.data.FileName);
      }
      setShowDownloadMessage(false);
    } catch (error) {
      setShowDownloadMessage(false);
      setShowError(true);
      setErrorMessage(t('downloadError'));
      logError(error);
    }
  };

  return (
    <PageArea>
      <Grid container>
        <Stack
          width="100%"
          spacing={2}
          direction={isMobile ? 'column' : 'row'}
          sx={{ justifyContent: 'space-between' }}
        >
          <PageTitle subTitle={t('subtitle')}>{t('heading')}</PageTitle>
        </Stack>

        <Box sx={{ width: '100%', mb: 1, mt: 2 }}>
          <Tabs
            value={tab}
            onChange={(_, v) => {
              setData([]);
              setIsLoading(true);
              setTab(v);
              setPage(0);
            }}
            aria-label="finance tabs"
          >
            <Tab label={t('invoicesTab')}
              value="invoices" />
            <Tab label={t('creditNotesTab')}
              value="creditNotes" />
            <Tab label={t('statementsTab')}
              value="statements" />
            <Tab label={t('rebatesTab')}
              value="rebates" />
            <Tab label={t('loyaltyTab')}
              value="loyalty" />
          </Tabs>
        </Box>

        <FinanceBox
          onDocumentDownload={onDocumentDownload}
          totalPages={totalPages}
          data={data}
          tableType={tab}
          isLoading={isLoading}
          page={page}
          setPage={setPage}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
        />
      </Grid>
      <MessagesSnackbar
        open={showDownloadMessage}
        onClose={() => setShowDownloadMessage(false)}
        message={t('downloading')}
        severity="info"
        duration={3000}
      />
      <MessagesSnackbar
        open={showError}
        onClose={() => setShowError(false)}
        message={errorMessage}
        severity="error"
        duration={5000}
      />
    </PageArea>
  );
};

export default Finance;
